import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWizardStatus } from '@/lib/api';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Rocket, ChevronRight, ClipboardCheck, Package, Truck, Target } from 'lucide-react';

const STEP_ICONS = [ClipboardCheck, Package, Truck, Target, Rocket];
const STEP_LABELS = ['CT Init', 'Stock', 'Delivery', 'Targets', 'Launch'];

export function MonthOpeningWidget({ month, state, district }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!state || state === 'all') return;
    try {
      const r = await getWizardStatus(month, state, district);
      setData(r.data);
    } catch { /* silent */ }
  }, [month, state, district]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const completed = data.completed_steps?.length ?? 0;
  const total = 5;
  const pct = Math.round((completed / total) * 100);
  const isComplete = data.status === 'completed';

  return (
    <button
      onClick={() => navigate('/month-opening')}
      className="w-full text-left rounded-lg border bg-card hover:bg-accent/50 transition-colors p-3 sm:p-4 group"
      data-testid="month-opening-widget"
    >
      <div className="flex items-center gap-3">
        <div className={`shrink-0 rounded-lg p-2 ${isComplete ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
          {isComplete
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            : <Rocket className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold truncate">
              Month Opening {month}
            </span>
            <span className={`text-xs font-bold tabular-nums ${isComplete ? 'text-emerald-600' : 'text-amber-600'}`}>
              {completed}/{total}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-1.5">
            <Progress value={pct} className="h-1.5 flex-1" />
            <span className="text-[10px] text-muted-foreground font-medium tabular-nums w-8 text-right">{pct}%</span>
          </div>

          <div className="flex items-center gap-1 mt-2">
            {STEP_LABELS.map((label, i) => {
              const stepNum = i + 1;
              const Icon = STEP_ICONS[i];
              const done = (data.completed_steps || []).includes(stepNum);
              return (
                <div
                  key={stepNum}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    done
                      ? 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                      : 'bg-muted/60 text-muted-foreground'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden sm:inline">{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
      </div>
    </button>
  );
}
